'use client'

import * as React from 'react'

import { ActionCard, type ActionCardTone } from './action-card'
import { Button } from './button'
import { Checkbox } from './checkbox'
import { Field, FieldLabel } from './field'
import { Input } from './input'
import { Textarea } from './textarea'

/**
 * FormCard — gather structured input from the user before the agent
 * proceeds. Used when the model's args have a known schema gap (e.g.
 * "I'll send the email — what's the subject and body?").
 *
 * Field kinds map to existing `@ordo/ui` input primitives so the
 * visual language stays consistent. Resolution payload is
 * `{ kind: 'form', values: Record<string, string|number|boolean> }`.
 *
 * Per `apps/agent/plans/plan-action-cards.md` Phase 3.
 */

export type FormFieldSpec =
  | {
      id: string
      label: string
      kind: 'text'
      placeholder?: string
      multiline?: boolean
      required?: boolean
      defaultValue?: string
    }
  | {
      id: string
      label: string
      kind: 'select'
      options: Array<{ value: string; label: string }>
      required?: boolean
      defaultValue?: string
    }
  | { id: string; label: string; kind: 'checkbox'; defaultValue?: boolean }
  | { id: string; label: string; kind: 'date'; required?: boolean; defaultValue?: string }
  | {
      id: string
      label: string
      kind: 'number'
      min?: number
      max?: number
      required?: boolean
      defaultValue?: number
    }

export interface FormCardProps {
  title: string
  description?: string
  attribution?: string
  fields: FormFieldSpec[]
  submitLabel?: string
  cancelLabel?: string
  onSubmit?: (values: Record<string, string | number | boolean>) => void
  onCancel?: () => void
  /** Once resolved, lock the form and show submitted values read-only. */
  resolvedValues?: Record<string, string | number | boolean>
}

function defaultFor(field: FormFieldSpec): string | number | boolean {
  if (field.kind === 'checkbox') {
    return field.defaultValue ?? false
  }
  if (field.kind === 'number') {
    return field.defaultValue ?? 0
  }
  return (field.defaultValue as string | undefined) ?? ''
}

export function FormCard({
  title,
  description,
  attribution,
  fields,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  onSubmit,
  onCancel,
  resolvedValues,
}: FormCardProps) {
  const resolved = resolvedValues !== undefined
  const initial = React.useMemo<Record<string, string | number | boolean>>(() => {
    if (resolved) {
      return resolvedValues!
    }
    const seed: Record<string, string | number | boolean> = {}
    for (const f of fields) {
      seed[f.id] = defaultFor(f)
    }
    return seed
  }, [fields, resolved, resolvedValues])

  const [values, setValues] = React.useState(initial)
  React.useEffect(() => {
    setValues(initial)
  }, [initial])

  const tone: ActionCardTone = resolved ? 'ok' : 'pending'

  const update = (id: string, v: string | number | boolean) => {
    if (resolved) {
      return
    }
    setValues((prev) => ({ ...prev, [id]: v }))
  }

  const isValid = React.useMemo(() => {
    for (const f of fields) {
      if ('required' in f && f.required) {
        const v = values[f.id]
        if (v === '' || v === undefined || v === null) {
          return false
        }
      }
    }
    return true
  }, [fields, values])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (resolved || !isValid) {
      return
    }
    onSubmit?.(values)
  }

  return (
    <ActionCard
      title={title}
      description={description}
      attribution={attribution}
      tone={tone}
      footer={
        resolved ? null : (
          <>
            <Button type="submit" form="action-card-form" size="sm" disabled={!isValid}>
              {submitLabel}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
          </>
        )
      }>
      <form id="action-card-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
        {fields.map((f) => (
          <Field key={f.id}>
            <FormFieldRow field={f} value={values[f.id]} disabled={resolved} onChange={(v) => update(f.id, v)} />
          </Field>
        ))}
      </form>
    </ActionCard>
  )
}

function FormFieldRow({
  field,
  value,
  disabled,
  onChange,
}: {
  field: FormFieldSpec
  value: string | number | boolean | undefined
  disabled: boolean
  onChange: (v: string | number | boolean) => void
}) {
  if (field.kind === 'checkbox') {
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          id={field.id}
          checked={Boolean(value)}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked === true)}
        />
        <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
      </div>
    )
  }
  if (field.kind === 'select') {
    return (
      <>
        <FieldLabel htmlFor={field.id}>
          {field.label}
          {field.required ? <span className="ml-0.5 text-destructive">*</span> : null}
        </FieldLabel>
        <select
          id={field.id}
          disabled={disabled}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm">
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </>
    )
  }
  if (field.kind === 'text' && field.multiline) {
    return (
      <>
        <FieldLabel htmlFor={field.id}>
          {field.label}
          {field.required ? <span className="ml-0.5 text-destructive">*</span> : null}
        </FieldLabel>
        <Textarea
          id={field.id}
          placeholder={field.placeholder}
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
        />
      </>
    )
  }
  if (field.kind === 'date') {
    return (
      <>
        <FieldLabel htmlFor={field.id}>
          {field.label}
          {field.required ? <span className="ml-0.5 text-destructive">*</span> : null}
        </FieldLabel>
        <Input
          id={field.id}
          type="date"
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      </>
    )
  }
  if (field.kind === 'number') {
    return (
      <>
        <FieldLabel htmlFor={field.id}>
          {field.label}
          {field.required ? <span className="ml-0.5 text-destructive">*</span> : null}
        </FieldLabel>
        <Input
          id={field.id}
          type="number"
          min={field.min}
          max={field.max}
          value={value === undefined ? '' : String(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
      </>
    )
  }
  // Default: single-line text
  return (
    <>
      <FieldLabel htmlFor={field.id}>
        {field.label}
        {'required' in field && field.required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </FieldLabel>
      <Input
        id={field.id}
        type="text"
        placeholder={'placeholder' in field ? field.placeholder : undefined}
        value={String(value ?? '')}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </>
  )
}
